import _ from 'lodash';
import { centerOfMass } from '@turf/turf';
import mapboxgl, { FullscreenControl } from 'mapbox-gl';

import { emptyGeojson } from './geojson';
import {
  mapLayers,
  STREET_LINES_LAYER_ID,
  STREETS_MARKERS,
} from '../constants/map';
import { getSectionBounds, getSectionGroupBounds } from './sections';
import { waitForSuccess } from './utils';

/**
 * Returns the source data for a given source ID.
 *
 * Sometimes `map.querySourceFeatures()` returns duplicates, so we filter them out.
 */
export const getSourceData = (map, sourceId) => {
  const queriedFeatures = map.querySourceFeatures(sourceId);
  return _.uniqBy(queriedFeatures, 'properties.id');
};

/**
 * Sets source data and waits for the data to actually load.
 */
export const setSourceData = async (map, sourceId, data) => {
  if (!map) throw new Error('map not defined');

  if (!data) throw new Error('Must provide data');
  if (!Array.isArray(data.features)) {
    throw new Error('Invalid data, must have features array');
  }

  const source = map.getSource(sourceId);
  if (!source) throw new Error('source not found');

  // Store some temp IDs so we can check to see when the data is updated
  const TEMP_ID = '_id';
  const tempIdMapping = {};
  data.features.forEach(({ properties }) => {
    const tempId = _.uniqueId('temp-id-');
    properties[TEMP_ID] = tempId; // eslint-disable-line no-param-reassign
    tempIdMapping[properties.id] = tempId;
  });

  // Set the data
  source.setData(data);

  // Wait for the data to actually be set
  return waitForSuccess(() => {
    const features = getSourceData(map, sourceId);

    return (
      features.length === data.features.length &&
      features.every(
        (feature) =>
          feature.properties[TEMP_ID] === tempIdMapping[feature.properties.id],
      )
    );
  });
};

/**
 * Adds empty reference layers to the map which are used for ordering
 * section specific layers.
 */
export const addReferenceLayers = (map) => {
  map.addSource('reference', emptyGeojson());
  Object.values(mapLayers).forEach((layerName) =>
    map.addLayer({
      id: layerName,
      type: 'circle',
      source: 'reference',
    }),
  );
};

export const forceMapResize = () => window.dispatchEvent(new Event('resize'));

export const flyTo = (feature, mapRef, options = {}) => {
  if (feature && mapRef.current) {
    const [lng, lat] = centerOfMass(feature).geometry.coordinates;
    const center = { lng, lat };
    mapRef.current.flyTo({
      center,
      ...options,
    });
  }
};

// Percentage of the map to pad the bounds by when fitting the bounds.
// Sections have more padding to give more surrounding context and
// because they are generally smaller than section groups.
const SECTION_BOUNDS_PADDING = 0.2;
const SECTION_GROUP_BOUNDS_PADDING = 0.1;

export const fitSectionBounds = (section, mapRef) => {
  const {
    offsetWidth: width,
    offsetHeight: height,
  } = mapRef.current.getContainer();
  const bounds = getSectionBounds(section);
  if (!bounds) return;
  mapRef.current.fitBounds(bounds, {
    padding: {
      top: SECTION_BOUNDS_PADDING * height,
      bottom: SECTION_BOUNDS_PADDING * height,
      left: SECTION_BOUNDS_PADDING * width,
      right: SECTION_BOUNDS_PADDING * width,
    },
    maxZoom: mapRef.current.getZoom(),
  });
};

export const fitSectionGroupBounds = (sectionGroup, sections, mapRef) => {
  const {
    offsetWidth: width,
    offsetHeight: height,
  } = mapRef.current.getContainer();
  mapRef.current.fitBounds(getSectionGroupBounds(sectionGroup, sections), {
    padding: {
      top: SECTION_GROUP_BOUNDS_PADDING * height,
      bottom: SECTION_GROUP_BOUNDS_PADDING * height,
      left: SECTION_GROUP_BOUNDS_PADDING * width,
      right: SECTION_GROUP_BOUNDS_PADDING * width,
    },
  });
};

export const addLocation = (mapRef) => {
  mapRef.addControl(
    new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showUserHeading: true,
    }),
  );
};

export const hideLayer = (map, layerId) => {
  map.setLayoutProperty(layerId, 'visibility', 'none');
};

export const showLayer = (map, layerId) => {
  map.setLayoutProperty(layerId, 'visibility', 'visible');
};

export const setLayerVisibility = (map, layerId, isVisible) =>
  isVisible ? showLayer(map, layerId) : hideLayer(map, layerId);

export const setMultiLayerVisibility = (map, visibilityMapList) => {
  visibilityMapList.forEach(({ layerId, isVisible }) =>
    setLayerVisibility(map, layerId, isVisible),
  );
};

const isRoadsLayer = (layer) => {
  return STREETS_MARKERS.some((marker) => layer.id.includes(marker));
};

export const setRoads = (map, roadsEnabled, showRoadLinesIfEnabled) => {
  map.getStyle().layers.forEach((layer) =>
    isRoadsLayer(layer)
      ? // street outline layer (used only on no-satellite view) is special case
        layer.id === STREET_LINES_LAYER_ID
        ? setLayerVisibility(
            map,
            layer.id,
            showRoadLinesIfEnabled && roadsEnabled,
          )
        : setLayerVisibility(map, layer.id, roadsEnabled)
      : null,
  );
};

export const resetMapLayers = (map, layerNames) => {
  // eslint-disable-next-line no-underscore-dangle
  if (!map || map._removed) {
    return;
  }
  Object.values(layerNames).forEach((layer) => {
    const mapLayer = map.getLayer(layer);
    if (typeof mapLayer !== 'undefined') {
      map.removeLayer(layer);
    }
  });
  Object.values(layerNames).forEach((layer) => {
    const mapSource = map.getSource(layer);
    if (typeof mapSource !== 'undefined') {
      map.removeSource(layer);
    }
  });
};

export const addImageToMap = (map, imageName, imagePath, options) => {
  // eslint-disable-next-line no-underscore-dangle
  if (!map || map._removed) return;

  if (!map.hasImage(imageName)) {
    map.loadImage(imagePath, (error, image) => {
      if (error) throw error;

      map.addImage(imageName, image, options);
    });
  }
};

/**
 * Toggle fullscreen state of `map`.
 *
 * This is a little hacky since it uses internal APIs. Note that this function
 * must be called as a part of a user interaction (e.g. a click event) or it
 * will be blocked by the browser.
 *
 * @param {mapboxgl.Map} map current map instance
 * @param {Event} evt user interaction event
 */
export const toggleFullscreen = (map, evt) => {
  if (!evt) return;

  // eslint-disable-next-line no-underscore-dangle
  const fullscreenControl = map?._controls?.find(
    (c) => c instanceof FullscreenControl,
  );

  if (fullscreenControl) {
    // eslint-disable-next-line no-underscore-dangle
    fullscreenControl._onClickFullscreen();
  }
};

/**
 * Resize the mapbox container element and update the map size. Returns a
 * cleanup function to restore the original size.
 */
export const resizeMapContainer = (map, { width, height }) => {
  const container = map.getContainer();
  container.setAttribute(
    'style',
    `width:${width}px !important; height:${height}px !important`,
  );
  map.resize();

  const restoreMapContainer = () => {
    container.style.removeProperty('width');
    container.style.removeProperty('height');
    map.resize();
  };

  return { restoreMapContainer };
};

/**
 * Set the attribution control on the map.
 *
 * Removes the old attribution control, adds the new one, and updates the ref.
 */
export const setAttributionControl = (
  map,
  attributionControl,
  attributionControlRef,
) => {
  if (!map || map._removed) return;
  const ref = attributionControlRef;
  if (ref?.current && map.hasControl(ref.current)) {
    map.removeControl(ref.current);
  }
  map.addControl(attributionControl, 'bottom-right');
  ref.current = attributionControl;
};
